/**
 * Conversation limit ("modo cola") — REST layer for the 6 routes in spec §5,
 * on the main axios client (Bearer token + Sentry trace headers).
 *
 * Errors arrive from the src/axios.js interceptor as
 * `{ message: <response body>, internalCode, httpCode }`, where the body is
 * the envelope `{ error: { code, message } }`.
 *
 * R8: `/me` and `/fill` never reject. Any failure — a 404 while the backend is
 * not deployed yet, a network error, a 500 — means `{ enabled: false }`, so
 * the chat loads exactly as it does today.
 */
import * as Sentry from '@sentry/react';

import API from '../../axios';
import { FAILED_VIEW, normalizeCapacityView, normalizeConfig, normalizeLoad } from './normalize';
import { RELEASE_COMMENT_MAX, RELEASE_REASON_VALUES } from './capacity';

const BASE = '/conversation-limit';

/**
 * `/me` and `/fill` sit in front of the chat load: a slow backend must not
 * keep the inbox behind a spinner. After this, the request is treated as a
 * failure (→ disabled) and the chat loads as usual.
 */
export const SESSION_REQUEST_TIMEOUT_MS = 4000;

const reportServerError = (error, op) => {
  const httpCode = error && error.httpCode;
  if (typeof httpCode === 'number' && httpCode >= 500) {
    Sentry.captureException(error, { tags: { component: 'conversationLimit', op } });
  }
};

const viewOrDisabled = async (request, op) => {
  try {
    const { data } = await request();
    return normalizeCapacityView(data);
  } catch (error) {
    reportServerError(error, op);
    return FAILED_VIEW;
  }
};

/** Heartbeat + capacity. Never rejects. */
export const getMyCapacity = () =>
  viewOrDisabled(() => API.get(`${BASE}/me`, { timeout: SESSION_REQUEST_TIMEOUT_MS }), 'me');

/** Presence + fill free seats from the queue. Never rejects. */
export const fillSeats = () =>
  viewOrDisabled(() => API.post(`${BASE}/fill`, {}, { timeout: SESSION_REQUEST_TIMEOUT_MS }), 'fill');

const invalidRequest = (message) => {
  const error = new Error(message);
  error.code = 'INVALID_REQUEST';
  return error;
};

export const releaseConversation = async ({ clientId, reason, comment } = {}) => {
  if (typeof clientId !== 'string' || !clientId) throw invalidRequest('clientId');
  if (!RELEASE_REASON_VALUES.includes(reason)) throw invalidRequest('reason');
  const trimmed = typeof comment === 'string' ? comment.trim() : '';
  if (trimmed.length > RELEASE_COMMENT_MAX) throw invalidRequest('comment');

  const body = trimmed ? { clientId, reason, comment: trimmed } : { clientId, reason };
  const { data } = await API.post(`${BASE}/release`, body);
  return normalizeCapacityView(data);
};

export const getAgentLoad = async () => {
  const { data } = await API.get(`${BASE}/load`);
  return normalizeLoad(data);
};

export const getLimitConfig = async () => {
  const { data } = await API.get(`${BASE}/config`);
  return normalizeConfig(data);
};

/** `payload` must come from `buildConfigPayload` (already validated). */
export const saveLimitConfig = async (payload) => {
  const { data } = await API.put(`${BASE}/config`, payload);
  return normalizeConfig(data);
};

/** `LIMIT_REACHED | NOT_OWNER | FORBIDDEN | ... | null`. Never throws. */
export const getConversationLimitErrorCode = (error) => {
  if (!error || typeof error !== 'object') return null;
  if (typeof error.code === 'string') return error.code;
  const body = error.message;
  if (!body || typeof body !== 'object') return null;
  if (body.error && typeof body.error.code === 'string') return body.error.code;
  return typeof body.code === 'string' ? body.code : null;
};

const ERROR_MESSAGES = {
  LIMIT_REACHED: 'El agente ya llegó a su límite de conversaciones.',
  NOT_OWNER: 'Esta conversación ya no está en tu cupo.',
  FORBIDDEN: 'No tienes permiso para hacer esto.',
  FEATURE_DISABLED: 'El límite de conversaciones no está activo en tu empresa.',
  INVALID_REQUEST: 'Revisa los datos e intenta de nuevo.',
  NOT_FOUND: 'No encontramos la conversación.',
};

const FALLBACK_MESSAGE = 'Ocurrió un error. Intenta de nuevo.';

export const conversationLimitErrorMessage = (error) =>
  ERROR_MESSAGES[getConversationLimitErrorCode(error)] || FALLBACK_MESSAGE;
